import Head from "next/head";
import Link from "next/link";
import { Stack } from "@mui/material";

import { SSText } from "src/components/base/components/SSText";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Cincinnati Reds Upgrades",
    description:
      "Enhance your game-day experience by upgrading your Cincinnati Reds tickets here. Upgrades are non-refundable.",
    authRedirectPage: routes.home,
    buttonLabel: "View Upgrades",
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How come I don't see seat upgrades available for purchase?",
    answer:
      "Upgrades are only eligible for those with a game ticket already. If you do not have a ticket for the game, please go to Reds Ticket Information to purchase.",
  },
  {
    question: "Will there be seat upgrades available for every game?",
    answer:
      "Upgrades are subject to availability, please check back in on the day of the game to see eligible offers.",
  },
  {
    question: "Where are my tickets?",
    answer:
      "After successful purchase, seat upgrades will be delivered within your Ballpark App.",
  },
  {
    question: "Where will my seats be located?",
    answer:
      "Seats will be located in the sections noted in your seat upgrade offer.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Seat upgrades are non transferrable and not eligible for resale.",
  },
  {
    question: "Can my seats be changed?",
    answer:
      "Once tickets have been sent to your account, they cannot be changed. Please head to Ticket Resolution for any assistance you may require.",
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    In connection with your purchase, you will receive ticket-related commercial
    emails from the Cincinnati Reds. You may unsubscribe at any time. By
    checking this box you agree to receive additional commercial emails from the
    Cincinnati Reds and MLB. The terms and conditions of your ticket and
    policies applicable to its use are updated to address circumstances related
    to COVID-19 and health and safety. By clicking the Purchase Tickets button,
    you agree to accept all Terms and Conditions. Further, due to the COVID-19
    pandemic, scheduling may be impacted; games may be canceled, rescheduled, or
    postponed or games may be held with limited capacity. Upon completing your
    purchase by selecting Purchase Tickets you acknowledge and agree that your
    credit card will be charged. By clicking &quot;Purchase Tickets&quot;, you
    agree to the{" "}
    <Link
      href={
        "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
      }
      target="_blank"
    >
      MLB Privacy Policy
    </Link>
    ,{" "}
    <Link
      href={
        "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
      }
      target="_blank"
    >
      Terms of Use
    </Link>
    , Cincinnati Reds{" "}
    <Link
      target="_blank"
      href="https://www.mlb.com/app/ballpark/reds/tickets/info/terms-conditions"
    >
      Terms & Conditions
    </Link>{" "}
    and to receive your ticket(s) via an SMS text message to the mobile #
    associated with your Reds account. Message and data rates may apply. Tickets
    will be made available in the MLB Ballpark mobile application only. MLB and
    supported mobile devices required.
  </>
);

export const NavSubmenuLinks = () => {
  const theme = useSSTheme();

  const links = [
    {
      id: 2,
      label: "MLB Privacy Policy",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/privacy-policy",
          "_self"
        );
      },
    },
    {
      id: 3,
      label: "MLB Terms & Conditions",
      onClick: () => {
        window.open(
          "https://www.mlb.com/app/ballpark/official-information/terms-of-use",
          "_self"
        );
      },
    },
  ];

  return (
    <>
      {links.map((link) => {
        return (
          <Stack
            key={link.id}
            spacing={2}
            direction={"row"}
            alignItems={"center"}
            onClick={() => link.onClick()}
            p={1}
            sx={{
              cursor: "pointer",
              pointerEvents: "auto",
              borderRadius: 2,
              "& .MuiSvgIcon-root": {
                color: theme.colors.gray[200],
              },
              "&:hover": {
                backgroundColor: theme.colors.gray[100],
              },
            }}
          >
            <SSText color={theme.colors.gray[300]}>{link.label}</SSText>
          </Stack>
        );
      })}
    </>
  );
};

export const FooterLinks = () => {
  const theme = useSSTheme();
  return (
    <>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/privacy-policy"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Privacy Policy
      </SSText>
      <SSText
        variant="body"
        onClick={() => {
          window.open(
            "https://www.mlb.com/app/ballpark/official-information/terms-of-use"
          );
        }}
        sx={{
          cursor: "pointer",
          color: theme.colors.common.white,
          textDecoration: "underline",
          textUnderlineOffset: "4px",
        }}
      >
        MLB Terms & Conditions
      </SSText>
    </>
  );
};

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-tik-tok-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !(function (w, d, t) {
                w.TiktokAnalyticsObject = t;
                var ttq = (w[t] = w[t] || []);
                (ttq.methods = [
                  "page",
                  "track",
                  "identify",
                  "instances",
                  "debug",
                  "on",
                  "off",
                  "once",
                  "ready",
                  "alias",
                  "group",
                  "enableCookie",
                  "disableCookie",
                ]),
                  (ttq.setAndDefer = function (t, e) {
                    t[e] = function () {
                      t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
                    };
                  });
                for (var i = 0; i < ttq.methods.length; i++)
                  ttq.setAndDefer(ttq, ttq.methods[i]);
                (ttq.instance = function (t) {
                  for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
                    ttq.setAndDefer(e, ttq.methods[n]);
                  return e;
                }),
                  (ttq.load = function (e, n) {
                    var i = "https://analytics.tiktok.com/i18n/pixel/events.js";
                    (ttq._i = ttq._i || {}),
                      (ttq._i[e] = []),
                      (ttq._i[e]._u = i),
                      (ttq._t = ttq._t || {}),
                      (ttq._t[e] = +new Date()),
                      (ttq._o = ttq._o || {}),
                      (ttq._o[e] = n || {});
                    n = document.createElement("script");
                    (n.type = "text/javascript"),
                      (n.async = !0),
                      (n.src = i + "?sdkid=" + e + "&lib=" + t);
                    e = document.getElementsByTagName("script")[0];
                    e.parentNode.insertBefore(n, e);
                  });

                ttq.load("CBP9R33C77UB6N07V05G");
                ttq.page();
                console.log('tiktok pixel page view fired.');
              })(window, document, "ttq");

              window.addEventListener("${constants.customEvents.purchaseSuccess}", async function(e) {
                const { cartRes, checkoutRes, passes, user } = e.detail;

                ttq.identify({
                  email: user.email,
                  phone_number: "+1" + user.phoneNumber,
                });

                if(cartRes.products && cartRes.products.length) {
                  for(let product of cartRes.products){
                    ttq.track("CompletePayment", {
                      content_id: product.eventCode,
                      content_name: product.productTitle,
                      quantity: String(product.seatCount),
                      price: String(product.price),
                      value: String(product.price * product.seatCount),
                      currency: "USD",
                    });
                    await new Promise(resolve => setTimeout(resolve, 500)); // 500ms delay
                  }
                };



                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_id = passDetail.eventCode;
                  content_name = passDetail.passTitle;
                  price = String(passDetail.price);
                  quantity = String(cartRes.passes[0].seatCount);

                  ttq.track("CompletePayment", {
                    content_id: passDetail.eventCode,
                    content_name: passDetail.passTitle,
                    quantity: String(cartRes.passes[0].seatCount),
                    price: String(passDetail.price),
                    value: String(cartRes.totalAmount),
                    currency: "USD",
                  });
                };
                console.log('tik tok pixel purchase event fired.');
              });
            `,
        }}
      />

      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '368362963346231');
              fbq('track', 'PageView');
              window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
                try{
                  const { cartRes, checkoutRes, passes } = e.detail;

                  let content_name = "";
                  if(cartRes.products && cartRes.products.length) {
                    content_name = "${TEAM_SCHEMA}_single_game_purchases";
                  };
                  if(cartRes.passes && cartRes.passes.length && passes.length ) {
                    const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                    content_name = passDetail.passTitle;
                  };

                  fbq('track', 'Purchase', {
                    content_name: content_name, 
                    value: String(cartRes.totalAmount), 
                    currency: 'USD', 
                    order_id: checkoutRes.orderNumber,
                  });
                  console.log('meta pixel purchase event fired.');
                
                } catch(error){
                  console.log('meta pixel purchase event error:', error); 
                }
              });
            `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=368362963346231&ev=PageView&noscript=1"
        />
      </noscript>
    </Head>
  );
};
